import type { Ref } from "react";
import React, { forwardRef, useState } from "react";
import type { ParseKeys } from "i18next";
import { useTranslation } from "react-i18next";

import {
  fileListIterator,
  getFileTypeFormat,
  validateFileList,
} from "./helper";
import type { IHiddenFileInputProps } from "./types";

import { notify } from "@/components/atoms/notify";
import { useReadOnly } from "@/hooks";

export const HiddenFileInput = forwardRef(function HiddenFileInput(
  props: IHiddenFileInputProps,
  ref: Ref<HTMLInputElement>,
) {
  const {
    maxSize = { size: 50, unit: "MB" },
    onChangeValue,
    type,
    readOnly: propsReadOnly,
    size,
    maxFilesLength = Infinity,
    concatFilesToOldValue = false,
    value,
    ...rest
  } = props;
  const { t } = useTranslation("form");
  const [inputValue] = useState("");
  const readOnly = useReadOnly(propsReadOnly);

  function setTranslatedError(errorKey: ParseKeys<"form">) {
    notify.error({
      title: t("errors.default"),
      text: t(errorKey, { count: maxFilesLength }),
    });
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    changeHandler(files);
  };

  function changeHandler(files: FileList | null): void {
    const fileList = fileListIterator(files, value);

    if (
      !readOnly &&
      !rest.disabled &&
      validateFileList(
        fileList,
        type,
        maxSize,
        maxFilesLength,
        setTranslatedError,
      )
    ) {
      if (concatFilesToOldValue) onChangeValue?.(fileList);
      else {
        const fileList = fileListIterator(files, []);

        onChangeValue?.(fileList);
      }
    }
  }
  return (
    <input
      type={"file"}
      ref={ref}
      style={{ opacity: "0", width: "0", height: "0", overflow: "hidden" }}
      accept={getFileTypeFormat(type).join(", ")}
      multiple
      value={inputValue}
      onChange={handleInputChange}
      readOnly={readOnly}
      {...rest}
    />
  );
});
